// 간편접수 담당 선택 — 칸을 켜고, 구성원마다 공개 여부·짧은 소개를 정한다(대표만).

import { useState, useTransition } from "react";
import { toast } from "sonner";

import { InstantSaveNote } from "@/components/save-bar";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Switch } from "@buildpy/ui/switch";
import { settleRedirect } from "@/lib/redirect-error";
import { SettingsResult } from "@buildpy/core/modules/org/action-types";
import { saveIntakeDesignerChoice, saveMemberIntake } from "@/api/actions/org";
import { MEMBER_INTRO_MAX } from "@buildpy/core/modules/org/types";

export interface IntakeMemberView {
  id: string;
  name: string;
  intake_visible: boolean;
  intro: string | null;
}

interface Props {
  canEdit: boolean;
  enabled: boolean;
  members: IntakeMemberView[];
}

const run = async (call: () => Promise<SettingsResult>): Promise<boolean> => {
  try {
    const result = await call();
    if (result.ok) toast.success(result.message);
    else toast.error(result.message);
    return result.ok;
  } catch (caught) {
    if (settleRedirect(caught)) return false;
    toast.error("저장하지 못했습니다. 잠시 후 다시 시도하세요.");
    return false;
  }
};

const MemberRow = ({ canEdit, member }: { canEdit: boolean; member: IntakeMemberView }) => {
  const [visible, setVisible] = useState(member.intake_visible);
  const [intro, setIntro] = useState(member.intro ?? "");
  const [pending, startTransition] = useTransition();

  const onSave = () => {
    startTransition(async () => {
      await run(() => saveMemberIntake({ memberId: member.id, intake_visible: visible, intro }));
    });
  };

  const switchId = `intake-visible-${member.id}`;
  return (
    <li className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center" data-testid="settings-intake-member">
      <label className="flex min-h-10 min-w-32 items-center gap-x-2 text-sm" htmlFor={switchId}>
        <Switch
          aria-label={`${member.name} 간편접수에 보이기`}
          checked={visible}
          disabled={!canEdit || pending}
          id={switchId}
          onCheckedChange={setVisible}
        />
        <span className="font-medium" id={`${switchId}-label`}>
          {member.name}
        </span>
      </label>
      <Input
        aria-label={`${member.name} 소개`}
        disabled={!canEdit || pending}
        maxLength={MEMBER_INTRO_MAX}
        name={`intake-intro-${member.id}`}
        onChange={(event) => setIntro(event.target.value)}
        placeholder="짧은 소개 (예: 아파트 전체 리모델링 10년)"
        value={intro}
      />
      {canEdit && (
        <Button className="shrink-0" disabled={pending} onClick={onSave} size="sm" type="button" variant="outline">
          저장
        </Button>
      )}
    </li>
  );
};

const SettingsIntake = ({ canEdit, enabled, members }: Props) => {
  const [checked, setChecked] = useState(enabled);
  const [pending, startTransition] = useTransition();

  const onToggle = (next: boolean) => {
    const prev = checked;
    setChecked(next);
    startTransition(async () => {
      if (!(await run(() => saveIntakeDesignerChoice(next)))) setChecked(prev);
    });
  };

  return (
    <SectionCard
      data-testid="settings-intake"
      description="켜면 간편접수에 「담당 디자이너 (선택)」 칸이 생깁니다. 공개한 구성원의 이름과 소개만 보입니다."
      actions={canEdit ? <InstantSaveNote /> : undefined}
      title="간편접수 담당 선택">
      <div className="flex flex-col gap-y-3">
        <label className="flex min-h-10 items-center gap-x-2 text-sm" htmlFor="intake-designer-choice">
          <Switch
            aria-describedby="intake-designer-choice-label"
            aria-label="고객이 담당 고르기"
            checked={checked}
            disabled={!canEdit || pending}
            id="intake-designer-choice"
            onCheckedChange={onToggle}
          />
          <span id="intake-designer-choice-label">{checked ? "고객이 담당을 고를 수 있습니다" : "담당 선택 칸을 숨깁니다"}</span>
        </label>
        <ul className="flex flex-col gap-2">
          {members.map((member) => (
            <MemberRow canEdit={canEdit} key={member.id} member={member} />
          ))}
        </ul>
      </div>
    </SectionCard>
  );
};

export default SettingsIntake;
